import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, ArrowRight } from 'lucide-react';
import { imgUrl } from '../utils/imgUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_LABEL = { 'product-videos': 'Product Video', 'instructional-videos': 'How-To' };

const Player = ({ v }) =>
  v.youtubeId ? (
    <iframe
      src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}?rel=0&playsinline=1`}
      title={v.title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      loading="lazy"
      className="w-full h-full border-0"
      data-testid={`pd-video-iframe-${v.slug}`}
    />
  ) : (
    <video
      src={imgUrl(v.src)}
      controls
      playsInline
      preload="metadata"
      className="w-full h-full bg-black"
      data-testid={`pd-video-player-${v.slug}`}
    />
  );

export const ProductVideos = ({ product }) => {
  const [videos, setVideos] = useState([]);
  const slug = product?.slug;

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    axios
      .get(`${API}/videos`, { params: { product: slug } })
      .then((r) => { if (alive) setVideos(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  if (videos.length === 0) return null;

  return (
    <section className="mb-12 sm:mb-16" data-testid="product-videos">
      <div className="flex items-end justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-600 mb-2">
            <Play size={14} fill="currentColor" /> Watch it in action
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">See the {product.name.replace(/ Set of 2| \(Set of 2\)/i, '')} in the Pool</h2>
        </div>
        <Link to="/videos" data-testid="product-videos-all-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 hover:text-cyan-700">
          All videos <ArrowRight size={15} />
        </Link>
      </div>
      <div className={`grid gap-6 ${videos.length === 1 ? 'grid-cols-1 max-w-3xl' : 'grid-cols-1 md:grid-cols-2'}`}>
        {videos.map((v) => (
          <div key={v.slug} className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100" data-testid={`product-video-${v.slug}`}>
            <div className="aspect-video w-full bg-black">
              <Player v={v} />
            </div>
            <div className="p-5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded-full">
                {SECTION_LABEL[v.section]}
              </span>
              <h3 className="text-base font-semibold text-gray-900 mt-3 line-clamp-2">{v.title}</h3>
              <Link
                to={`/videos/${v.section}/${v.slug}`}
                data-testid={`product-video-link-${v.slug}`}
                className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-cyan-600 hover:text-cyan-700"
              >
                Open video page <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProductVideos;
